import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import ReopenActiveBanner from './ReopenActiveBanner';
import CFOLiveStrip from './CFOLiveStrip';
import { 
  LayoutDashboard, 
  FolderKanban, 
  FileText, 
  Users, 
  User,
  Wallet, 
  Package, 
  TrendingUp, 
  BarChart3,
  LogOut,
  Menu,
  X,
  Landmark,
  UserCog,
  BookOpen,
  ClipboardList,
  Truck,
  GitBranch,
  ShieldCheck,
  ShieldAlert,
  ShoppingCart,
  FileBarChart,
  CreditCard,
  CheckSquare,
  Settings,
  FileSignature,
  Download,
  Building2,
  Building,
  Zap,
  PieChart,
  MapPin,
  Database,
  Calculator,
  Navigation,
  RefreshCw,
  Scale,
  Target,
  FilePlus,
  Bell,
  HardHat,
  Hammer,
  Lock,
  Unlock,
  Car,
  Folder,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Workflow,
  Brain,
  Activity,
  Gauge,
  Sparkles,
  ClipboardCheck,
  PackageCheck,
  Flame,
  FileCheck2,
  FileMinus,
  Archive,
  ListChecks,
  Banknote,
  Briefcase,
  Mail,
  HardDrive,
  Receipt,
  Tag,
  Undo2,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import LanguageToggle from './LanguageToggle';
import { CommandBarLauncher } from './CommandBar';
import CompanySwitcher from './CompanySwitcher';
import PilotSidebar from './PilotSidebar';
import PilotWelcomeTour from './PilotWelcomeTour';

const Layout = ({ children, user }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [criticalAlerts, setCriticalAlerts] = useState(0);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Pilot Snapshot — when nav_mode is on, non-admin users see a curated
  // 5-item PilotSidebar overlay instead of the full 50+ item sidebar.
  // Admins always see the full sidebar so they can operate + observe.
  const [pilotNavMode, setPilotNavMode] = useState(false);
  // Tenant identity for the full-sidebar header (dynamic, NEVER hardcoded).
  const [tenantIdentity, setTenantIdentity] = useState(null);
  // Pilot Welcome Tour — shown once after first login for pilot_admin role.
  const [showPilotTour, setShowPilotTour] = useState(false);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axiosInstance.get('/pilot/status').catch(() => ({ data: {} })),
      axiosInstance.get('/companies/me/active').catch(() => ({ data: null })),
    ]).then(([statusRes, identityRes]) => {
      if (cancelled) return;
      setPilotNavMode(!!statusRes?.data?.nav_mode);
      setTenantIdentity(identityRes?.data || null);
    });
    return () => { cancelled = true; };
  }, []);
  const usePilotSidebar = pilotNavMode && user?.role !== 'admin';
  // Debug override for QA — append `?pilot=1` to any URL to force the
  // Pilot Sidebar render path even when logged in as admin. Useful for
  // dry-run testing without creating a new non-admin account.
  const _debugPilot = typeof window !== 'undefined' && window.location.search.includes('pilot=1');
  const usePilotSidebarFinal = usePilotSidebar || _debugPilot;

    // Poll critical-alert count every 60s + instant refresh on audit actions
  useEffect(() => {
    let cancelled = false;
    const fetchBanner = async () => {
      try {
        const { axiosInstance } = await import('../App');
        const r = await axiosInstance.get('/audit-intel/banner');
        if (!cancelled) {
          setCriticalAlerts(r.data?.critical_open || 0);
          if ((r.data?.critical_open || 0) === 0) setBannerDismissed(false);
        }
      } catch (e) { /* silent */ }
    };
    fetchBanner();
    const id = setInterval(fetchBanner, 60000);
    window.addEventListener('audit-refresh', fetchBanner);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('audit-refresh', fetchBanner);
    };
  }, []);

  // Pilot Welcome Tour visibility — show once for `pilot_admin` whose
  // tour stamp is still null on /api/auth/me.
  useEffect(() => {
    if (user?.role === 'pilot_admin' && !user?.pilot_tour_completed_at) {
      // Small delay so the dashboard renders behind the modal first.
      const t = setTimeout(() => setShowPilotTour(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  // Check if user is admin - handle both string and lowercase
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  // Listen for PWA install prompt
  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    setIsAppInstalled(isStandalone);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    // If already installed
    if (isAppInstalled) {
      alert('التطبيق مُثبت بالفعل على جهازك!\n\nApp is already installed on your device!');
      return;
    }

    // If we have the install prompt, use it
    if (installPrompt) {
      try {
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsAppInstalled(true);
        }
        setInstallPrompt(null);
      } catch (error) {
        console.error('Install error:', error);
      }
      return;
    }
    
    // Detect browser and show appropriate instructions
    const userAgent = navigator.userAgent.toLowerCase();
    const isChrome = userAgent.includes('chrome') && !userAgent.includes('edg');
    const isEdge = userAgent.includes('edg');
    const isFirefox = userAgent.includes('firefox');
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
    const isMobile = /android|iphone|ipad|ipod/.test(userAgent);

    let instructions = '';
    
    if (isMobile && isSafari) {
      instructions = 'لتثبيت التطبيق على Safari:\n\n' +
        '1. اضغط على زر المشاركة (مربع مع سهم للأعلى)\n' +
        '2. اختر "إضافة إلى الشاشة الرئيسية"\n' +
        '3. اضغط "إضافة"\n\n' +
        'سيظهر التطبيق على شاشتك الرئيسية';
    } else if (isMobile) {
      instructions = 'لتثبيت التطبيق:\n\n' +
        '1. اضغط على القائمة (⋮) في المتصفح\n' +
        '2. اختر "إضافة إلى الشاشة الرئيسية" أو "تثبيت التطبيق"\n' +
        '3. اضغط "تثبيت"\n\n' +
        'سيظهر التطبيق على شاشتك الرئيسية';
    } else if (isChrome || isEdge) {
      instructions = 'لتثبيت التطبيق:\n\n' +
        '1. ابحث عن أيقونة التثبيت (⊕) في شريط العنوان\n' +
        '   أو اضغط على القائمة (⋮)\n' +
        '2. اختر "تثبيت Contractor Books"\n' +
        '3. اضغط "تثبيت"\n\n' +
        'سيظهر التطبيق على سطح المكتب';
    } else if (isFirefox) {
      instructions = 'متصفح Firefox لا يدعم تثبيت التطبيقات بشكل كامل.\n\n' +
        'للحصول على أفضل تجربة، استخدم Chrome أو Edge.';
    } else if (isSafari) {
      instructions = 'متصفح Safari على الكمبيوتر لا يدعم تثبيت التطبيقات.\n\n' +
        'للحصول على أفضل تجربة، استخدم Chrome أو Edge.';
    } else {
      instructions = 'لتثبيت التطبيق:\n\n' +
        '1. استخدم متصفح Chrome أو Edge\n' +
        '2. ابحث عن أيقونة التثبيت في شريط العنوان\n' +
        '3. اضغط "تثبيت"';
    }
    
    alert(instructions);
  };

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await axiosInstance.get('/my-permissions');
        setUserPermissions(response.data.pages || []);
      } catch (error) {
        // Default to basic permissions on error
        setUserPermissions(['dashboard']);
      } finally {
        setPermissionsLoaded(true);
      }
    };
    
    if (user) {
      fetchPermissions();
    }
  }, [user]);

  // Page key to path mapping
  const pageKeyToPath = {
    dashboard: '/',
    approvals: '/approvals',
    inbox: '/inbox',
    projects: '/projects',
    contracts: '/contracts',
    quotations: '/quotations',
    leads_console: '/leads-console',
    rfq_console: '/rfq-console',
    customer_rfqs: '/customer-rfqs',
    price_comparisons: '/price-comparisons',
    quotation_analytics: '/quotation-analytics',
    quotation_approvals: '/quotation-approvals',
    approval_matrix: '/approval-matrix',
    expense_category_mappings: '/expense-category-mappings',
    invoices: '/invoices',
    zatca: '/zatca',
    zatca_setup: '/zatca/setup',
    onboarding: '/onboarding',
    purchase_orders: '/purchase-orders',
    supplier_bills: '/supplier-bills',
    customers: '/customers',
    suppliers: '/suppliers',
    item_master: '/item-master',
    vendor_quotations: '/vendor-quotations',
    rfqs: '/rfqs',
    customer_report: '/customer-report',
    customer_statement: '/customer-statement',
    supplier_report: '/supplier-report',
    supplier_statement: '/supplier-statement',
    accounts: '/accounts',
    bank: '/bank',
    payments: '/payments',
    journal: '/journal',
    users: '/users',
    employees: '/employees',
    payroll: '/payroll',
    employee_accounts: '/employee-accounts',
    inventory: '/inventory',
    petty_cash: '/petty-cash',
    assets: '/assets',
    budgets: '/budgets',
    expenses: '/expenses',
    expenses_report: '/expenses-report',
    reports: '/reports',
    settings: '/settings',
    backup: '/backup',
    visit_form: '/visit-form',
    visit_reports_system: '/visit-reports-system',
    field_visits: '/field-visits',
    vat_return: '/vat-return',
    vat_claims: '/vat-claims',
    bills_center: '/bills-center',
    financial_ops: '/financial-ops',
    bank_reconciliation: '/bank-reconciliation',
    bank_rules: '/bank-rules',
    government_services: '/government-services',
    expense_categories: '/expense-categories',
    credit_notes: '/credit-notes',
    cfo_dashboard: '/cfo-dashboard',
    cfo_email_preview: '/cfo-email-preview',
    reconciliation_center: '/reconciliation-center',
    financial_repair_center: '/financial-repair-center',
    data_quality_center: '/data-quality-center',
    system_diagnostic: '/system-diagnostic',
    cfo_actions: '/cfo-actions',
    insights: '/insights',
    year_end: '/year-end',
    my_tasks: '/my-tasks',
    instant_reports: '/instant-reports',
    accounting_reports: '/accounting-reports',
    month_end: '/month-end',
    period_reopen: '/period-reopen',
    s2p_review: '/pece/s2p',
    s2p_dashboard: '/pece/s2p/dashboard',
    mar_review: '/pece/mar',
    mir_review: '/pece/mir',
    deliveries: '/pece/deliveries',
    consumption: '/pece/consumption',
    pece_claims: '/pece/claims',
    dvl_truth: '/dvl-financial-dashboard',
    ecp_inspector: '/ecp',
    demo_center: '/admin/demo',
    cost_centers: '/cost-centers',
    progress_claims: '/progress-claims',
    variation_orders: '/variation-orders',
    vo_queue: '/vo-queue',
    equipment: '/equipment',
    cost_allocation: '/cost-allocation',
    subcontractors: '/subcontractors',
    subcontracts: '/subcontracts',
    subcontract_claims: '/subcontract-claims',
    fleet: '/fleet',
    documents: '/documents',
    governance: '/governance',
    audit_log: '/audit-log',
    cancellations_log: '/cancellations-log',
    data_health: '/data-health',
    audit_intelligence: '/audit-intelligence',
    data_integrity: '/data-integrity',
    legacy_audit: '/legacy-audit',
    security_dashboard: '/security-dashboard',
    security: '/security',
    archive_center: '/archive-center'
  };

  const allMenuItems = [
    { path: '/', icon: LayoutDashboard, label: t('sidebar.items.dashboard'), key: 'dashboard' },
    { path: '/inbox', icon: Workflow, label: 'My Work Queue', key: 'inbox' },
    { path: '/cfo-dashboard', icon: Building2, label: t('sidebar.items.cfo_dashboard', 'CFO Dashboard'), key: 'cfo_dashboard' },
    { path: '/commercial-hub', icon: Banknote, label: 'Commercial Hub', key: 'commercial_hub' },
    { path: '/compliance-center', icon: ShieldCheck, label: 'Compliance Center', key: 'compliance_center' },
    { path: '/cfo-pnl', icon: Briefcase, label: 'CFO PnL', key: 'cfo_pnl' },
    { path: '/contract-execution', icon: GitBranch, label: 'Contract Execution', key: 'contract_execution' },
    { path: '/audit-trail', icon: ShieldCheck, label: 'Audit Trail', key: 'audit_trail' },
    { path: '/credit-control', icon: Wallet, label: 'Credit Control', key: 'credit_control' },
    { path: '/cfo-pack', icon: Briefcase, label: 'CFO Monthly Pack', key: 'cfo_pack' },
    { path: '/external-accountant', icon: ShieldCheck, label: 'External Accountant Workspace', key: 'external_accountant' },
    { path: '/ai-insights', icon: Brain, label: 'AI Decision Cockpit', key: 'ai_insights' },
    { path: '/cfo-actions', icon: Zap, label: t('sidebar.items.cfo_actions', 'Action Center'), key: 'cfo_actions' },
    { path: '/insights', icon: Sparkles, label: t('sidebar.items.insights', 'Insights'), key: 'insights' },
    { path: '/approvals', icon: CheckSquare, label: t('sidebar.items.approvals'), key: 'approvals' },
    { path: '/my-tasks', icon: Workflow, label: t('sidebar.items.my_tasks', t('layout.my_tasks')), key: 'my_tasks' },
    { path: '/notifications', icon: Bell, label: t('sidebar.items.notifications'), key: 'notifications' },
    { path: '/reports', icon: BarChart3, label: t('sidebar.items.reports'), key: 'reports' },
    { path: '/instant-reports', icon: BarChart3, label: t('sidebar.items.instant_reports', 'Instant Reports'), key: 'instant_reports' },
    { path: '/expenses-report', icon: FileBarChart, label: t('sidebar.items.expenses_report'), key: 'expenses_report' },
    { path: '/expense-category-mappings', icon: ShieldCheck, label: 'Expense Category Mapping', key: 'expense_category_mappings' },

    { path: '/projects', icon: FolderKanban, label: t('sidebar.items.projects'), key: 'projects' },
    { path: '/project-execution', icon: Building, label: 'Project Execution Center', key: 'project_execution' },
    { path: '/pece/s2p', icon: Brain, label: t('sidebar.items.s2p', 'S2P Review Queue'), key: 's2p_review' },
    { path: '/pece/s2p/dashboard', icon: Gauge, label: t('sidebar.items.s2p_dashboard', 'S2P Effectiveness'), key: 's2p_dashboard' },
    { path: '/pece/mar', icon: ClipboardCheck, label: t('sidebar.items.mar', 'MAR Review Queue'), key: 'mar_review' },
    { path: '/pece/mir', icon: PackageCheck, label: t('sidebar.items.mir', 'MIR Review Queue'), key: 'mir_review' },
    { path: '/pece/deliveries', icon: Truck, label: t('sidebar.items.deliveries', 'Delivery Dashboard'), key: 'deliveries' },
    { path: '/pece/consumption', icon: Flame, label: t('sidebar.items.consumption', 'Consumption Reality'), key: 'consumption' },
    { path: '/pece/claims', icon: FileCheck2, label: t('sidebar.items.pece_claims', 'PECE Claims'), key: 'pece_claims' },
    { path: '/dvl-financial-dashboard', icon: ShieldCheck, label: t('sidebar.items.dvl_truth', 'Financial Truth'), key: 'dvl_truth' },
    { path: '/ecp', icon: Activity, label: t('sidebar.items.ecp', 'Event Inspector'), key: 'ecp_inspector' },
    { path: '/admin/demo', icon: Sparkles, label: t('sidebar.items.demo', 'Demo Center'), key: 'demo_center' },
    { path: '/onboarding', icon: Sparkles, label: t('sidebar.items.onboarding', 'Company Onboarding'), key: 'onboarding' },
    { path: '/contracts', icon: FileSignature, label: t('sidebar.items.contracts'), key: 'contracts' },
    { path: '/cost-centers', icon: Target, label: t('sidebar.items.cost_centers'), key: 'cost_centers' },
    { path: '/progress-claims', icon: FileBarChart, label: t('sidebar.items.progress_claims'), key: 'progress_claims' },
    { path: '/variation-orders', icon: FilePlus, label: t('sidebar.items.variation_orders'), key: 'variation_orders' },
    { path: '/vo-queue', icon: FileText, label: t('sidebar.items.vo_queue', 'VO Posting Queue'), key: 'vo_queue' },
    { path: '/budgets', icon: PieChart, label: t('sidebar.items.budgets'), key: 'budgets' },
    { path: '/equipment', icon: Truck, label: t('sidebar.items.equipment'), key: 'equipment' },
    { path: '/cost-allocation', icon: GitBranch, label: t('sidebar.items.cost_allocation'), key: 'cost_allocation' },

    { path: '/customer-rfqs', icon: FileText, label: 'Request for Quotation', key: 'customer_rfqs' },
    { path: '/quotations', icon: ClipboardList, label: t('sidebar.items.quotations'), key: 'quotations' },
    { path: '/leads-console', icon: Users, label: t('sidebar.items.leads_console', 'Leads → Quotation'), key: 'leads_console' },
    { path: '/rfq-console', icon: ListChecks, label: t('sidebar.items.rfq_console', 'RFQ → Quotation'), key: 'rfq_console' },
    { path: '/sales-quotations', icon: FileText, label: 'Sales Quotations', key: 'sales_quotations' },
    { path: '/quotation-analytics', icon: TrendingUp, label: 'Quotation Analytics', key: 'quotation_analytics' },
    { path: '/quotation-approvals', icon: ClipboardCheck, label: 'Approval Inbox', key: 'quotation_approvals' },
    { path: '/approval-matrix', icon: ShieldCheck, label: 'Approval Matrix', key: 'approval_matrix' },
    { path: '/customers', icon: Users, label: t('sidebar.items.customers'), key: 'customers' },
    { path: '/invoices', icon: FileText, label: t('sidebar.items.invoices'), key: 'invoices' },
    { path: '/payments', icon: CreditCard, label: t('sidebar.items.payments'), key: 'payments' },
    { path: '/customer-report', icon: BarChart3, label: t('sidebar.items.customer_report'), key: 'customer_report' },
    { path: '/customer-statement', icon: FileText, label: 'Customer Statement', key: 'customer_statement' },

    { path: '/suppliers', icon: Truck, label: t('sidebar.items.suppliers'), key: 'suppliers' },
    { path: '/financial-ops', icon: Activity, label: 'Financial Operations', key: 'financial_ops' },
    { path: '/bank-reconciliation', icon: Landmark, label: 'Bank Reconciliation', key: 'bank_reconciliation' },
    { path: '/bank-rules', icon: ShieldCheck, label: 'Bank Rules', key: 'bank_rules' },
    { path: '/bills-center', icon: Receipt, label: 'Bills Center', key: 'bills_center' },
    { path: '/government-services', icon: Landmark, label: 'Government Services', key: 'government_services' },
    { path: '/expense-categories', icon: Tag, label: 'Expense Categories', key: 'expense_categories' },
    { path: '/vat-claims', icon: Receipt, label: 'VAT Claim Management', key: 'vat_claims' },

    { path: '/item-master', icon: Package, label: 'Item Master', key: 'item_master' },
    { path: '/vendor-quotations', icon: FileText, label: 'Vendor Quotations', key: 'vendor_quotations' },
    { path: '/price-comparisons', icon: Scale, label: 'Comparison of Supplier Offers', key: 'price_comparisons' },
    { path: '/rfqs', icon: ShoppingCart, label: 'RFQ Workspace', key: 'rfqs' },
    { path: '/purchase-orders', icon: ShoppingCart, label: t('sidebar.items.purchase_orders'), key: 'purchase_orders' },
    { path: '/supplier-bills', icon: FileText, label: t('sidebar.items.supplier_bills'), key: 'supplier_bills' },
    { path: '/gov-utility-payments', icon: Landmark, label: 'Government & Utility Payments', key: 'gov_utility_payments' },
    { path: '/supplier-report', icon: BarChart3, label: t('sidebar.items.supplier_report'), key: 'supplier_report' },
    { path: '/supplier-statement', icon: FileText, label: 'Supplier Statement', key: 'supplier_statement' },

    { path: '/subcontractors', icon: HardHat, label: t('sidebar.items.subcontractors'), key: 'subcontractors' },
    { path: '/subcontracts', icon: FileSignature, label: t('sidebar.items.subcontracts'), key: 'subcontracts' },
    { path: '/subcontract-claims', icon: FileBarChart, label: t('sidebar.items.subcontract_claims'), key: 'subcontract_claims' },

    { path: '/accounts', icon: Landmark, label: t('sidebar.items.accounts'), key: 'accounts' },
    { path: '/journal', icon: BookOpen, label: t('sidebar.items.journal'), key: 'journal' },
    { path: '/bank', icon: Building2, label: t('sidebar.items.bank'), key: 'bank' },
    { path: '/petty-cash', icon: Wallet, label: t('sidebar.items.petty_cash'), key: 'petty_cash' },
    { path: '/expenses', icon: TrendingUp, label: t('sidebar.items.expenses'), key: 'expenses' },
    { path: '/credit-notes', icon: FileMinus, label: t('sidebar.items.credit_notes', 'Credit Notes'), key: 'credit_notes' },
    { path: '/accounting-reports', icon: Scale, label: t('sidebar.items.accounting_reports'), key: 'accounting_reports' },
    { path: '/zatca', icon: Building2, label: t('sidebar.items.zatca'), key: 'zatca' },
    { path: '/zatca/setup', icon: ShieldCheck, label: t('sidebar.items.zatca_setup', 'ZATCA Setup'), key: 'zatca_setup' },
    { path: '/month-end', icon: Lock, label: t('sidebar.items.month_end'), key: 'month_end' },
    { path: '/period-reopen', icon: Unlock, label: t('sidebar.items.period_reopen', 'Period Reopen Center'), key: 'period_reopen' },
    { path: '/overhead-report', icon: Calculator, label: t('sidebar.items.overhead_report', 'Overhead Simulator'), key: 'overhead_report' },
    { path: '/cfo-dashboard', icon: TrendingUp, label: t('sidebar.items.cfo_dashboard', 'CFO Dashboard'), key: 'cfo_dashboard' },
    { path: '/cfo-email-preview', icon: Mail, label: t('sidebar.items.cfo_email_preview', 'CFO Daily Brief'), key: 'cfo_email_preview' },
    { path: '/reconciliation-center', icon: ShieldCheck, label: t('sidebar.items.reconciliation_center', 'Reconciliation Center'), key: 'reconciliation_center' },
    { path: '/financial-repair-center', icon: Hammer, label: t('sidebar.items.financial_repair_center', 'Repair Center'), key: 'financial_repair_center' },
    { path: '/admin/financial-repair', icon: ShieldAlert, label: 'Cash Recon Forensics', key: 'admin_financial_repair' },
    { path: '/admin/coa-classification', icon: Wallet, label: 'CoA Classification', key: 'admin_coa_classification' },
    { path: '/admin/records-maintenance', icon: ShieldAlert, label: 'Records Maintenance', key: 'admin_records_maintenance' },
    { path: '/financial-timelines', icon: Activity, label: 'Financial Timelines', key: 'financial_timelines' },
    { path: '/payment-queue', icon: Wallet, label: 'Payment Queue', key: 'payment_queue' },
    { path: '/payment-runs', icon: Wallet, label: 'Payment Runs', key: 'payment_runs' },
    { path: '/bank/adjustments', icon: Wallet, label: 'Bank Adjustments', key: 'bank_adjustments' },
    { path: '/bank-import-manager', icon: Wallet, label: 'Bank Import Manager', key: 'bank_import_manager' },
    { path: '/bank-statements-registry', icon: Folder, label: 'Bank Statements Registry', key: 'bank_statements_registry' },
    { path: '/vat-returns', icon: Receipt, label: 'VAT Return', key: 'vat_returns' },
    { path: '/zatca/compliance', icon: ShieldCheck, label: 'ZATCA Compliance', key: 'zatca_compliance' },
    { path: '/matching-intelligence', icon: Activity, label: 'Matching Intelligence', key: 'matching_intelligence' },
    { path: '/autopost-insights', icon: Activity, label: 'Auto-Post Insights', key: 'autopost_insights' },
    { path: '/document-hub', icon: Folder, label: 'Document Hub', key: 'document_hub' },
    { path: '/migration-center', icon: Hammer, label: 'Migration Center', key: 'migration_center' },
    { path: '/system-health', icon: ShieldCheck, label: 'System Health', key: 'system_health' },
    { path: '/data-health', icon: Activity, label: t('sidebar.items.data_health', 'صحة البيانات'), key: 'data_health' },
    { path: '/low-memory-mode', icon: Activity, label: 'Low Memory Mode', key: 'low_memory_mode' },
    { path: '/data-quality-center', icon: ShieldAlert, label: t('sidebar.items.data_quality_center', 'Data Quality Center'), key: 'data_quality_center' },
    { path: '/system-diagnostic', icon: Activity, label: t('sidebar.items.system_diagnostic', 'System Diagnostic'), key: 'system_diagnostic' },
    { path: '/year-end', icon: ShieldCheck, label: t('sidebar.items.year_end', 'Year-End Closing'), key: 'year_end' },

    { path: '/employees', icon: Users, label: t('sidebar.items.employees'), key: 'employees' },
    { path: '/payroll', icon: Wallet, label: t('sidebar.items.payroll'), key: 'payroll' },
    { path: '/employee-accounts', icon: CreditCard, label: t('sidebar.items.employee_accounts'), key: 'employee_accounts' },

    { path: '/inventory', icon: Package, label: t('sidebar.items.inventory'), key: 'inventory' },
    { path: '/assets', icon: Building2, label: t('sidebar.items.assets'), key: 'assets' },
    { path: '/fleet', icon: Car, label: t('sidebar.items.fleet'), key: 'fleet' },

    { path: '/documents', icon: Folder, label: t('sidebar.items.documents'), key: 'documents' },

    { path: '/visit-form', icon: MapPin, label: t('sidebar.items.visit_form'), key: 'visit_form' },
    { path: '/visit-reports-system', icon: ClipboardList, label: t('sidebar.items.visit_reports_system'), key: 'visit_reports_system' },
    { path: '/field-visits', icon: Navigation, label: t('sidebar.items.field_visits'), key: 'field_visits' },

    { path: '/governance', icon: ShieldCheck, label: t('sidebar.items.governance'), key: 'governance' },
    { path: '/audit-log', icon: ShieldCheck, label: t('sidebar.items.audit_log', 'Audit Log'), key: 'audit_log' },
    { path: '/cancellations-log', icon: Undo2, label: t('sidebar.items.cancellations_log', 'سجل الإلغاءات'), key: 'cancellations_log' },
    { path: '/audit-intelligence', icon: ShieldAlert, label: t('sidebar.items.audit_intelligence', 'Audit Intelligence'), key: 'audit_intelligence' },
    { path: '/data-integrity', icon: Database, label: t('sidebar.items.data_integrity', 'Data Integrity'), key: 'data_integrity' },
    { path: '/legacy-audit', icon: Database, label: t('sidebar.items.legacy_audit', 'Legacy Recovery'), key: 'legacy_audit' },
    { path: '/security', icon: ShieldCheck, label: t('sidebar.items.security', 'Security & Sessions'), key: 'security' },
    { path: '/security-dashboard', icon: ShieldCheck, label: t('sidebar.items.security_dashboard', 'Security Dashboard'), key: 'security_dashboard' },
    { path: '/users', icon: UserCog, label: t('sidebar.items.users'), key: 'users' },
    { path: '/settings', icon: Settings, label: t('sidebar.items.settings'), key: 'settings' },
    { path: '/owner-controls', icon: ShieldCheck, label: t('sidebar.items.owner_controls', 'تحكم المالك · Owner Controls'), key: 'owner_controls' },
    { path: '/backup', icon: Database, label: t('sidebar.items.backup'), key: 'backup' },
    { path: '/storage-health', icon: HardDrive, label: t('sidebar.items.storage_health', 'Storage Health · حالة التخزين'), key: 'storage_health' },
    { path: '/archive-center', icon: Archive, label: t('sidebar.items.archive_center', 'Archive Center · مركز الأرشيف'), key: 'archive_center' },
  ];

  // Group definitions — each group lists the keys (in display order) that belong to it.
  // 2026-02 restructure (per user request):
  //   • CFO Dashboard & Action Center are now top-level under "Dashboard".
  //   • PECE Workspace is its own group, sitting between Finance and HR.
  //   • Credit Notes is a standalone item under Accounting & Finance.
  //   • Year-End Closing lives next to Month-End under Finance.
  // Sidebar restructure — Feb 2026:
  // The single "finance" group was split into 5 focused groups
  // (General Accounting / Banking & Treasury / Payments / Tax & Compliance
  // / Financial Intelligence) so the Flyout stays scannable and
  // newly added pages have a natural home. No page appears in more
  // than one group (SSOT for navigation).
  const MENU_GROUPS = [
    { id: 'dashboard',        label: t('sidebar.groups.dashboard'),        icon: LayoutDashboard, keys: ['inbox', 'dashboard', 'cfo_dashboard', 'cfo_email_preview', 'cfo_pnl', 'cfo_pack', 'ai_insights', 'cfo_actions', 'insights', 'approvals', 'my_tasks', 'notifications', 'reports', 'instant_reports', 'expenses_report'] },
    { id: 'projects',         label: t('sidebar.groups.projects'),         icon: FolderKanban,    keys: ['projects', 'project_execution', 'contracts', 'contract_execution', 'cost_centers', 'progress_claims', 'variation_orders', 'vo_queue', 'budgets', 'equipment', 'cost_allocation'] },
    { id: 'sales',            label: t('sidebar.groups.sales'),            icon: TrendingUp,      keys: ['customer_rfqs', 'rfq_console', 'leads_console', 'quotations', 'sales_quotations', 'quotation_analytics', 'quotation_approvals', 'approval_matrix', 'customers', 'invoices', 'commercial_hub', 'payments', 'customer_report', 'customer_statement', 'credit_control'] },
    { id: 'purchase',         label: t('sidebar.groups.purchase'),         icon: ShoppingCart,    keys: ['financial_ops', 'bills_center', 'government_services', 'gov_utility_payments', 'expense_categories', 'vat_claims', 'suppliers', 'item_master', 'vendor_quotations', 'price_comparisons', 'rfqs', 'purchase_orders', 'supplier_bills', 'supplier_report', 'supplier_statement'] },
    { id: 'subc',             label: t('sidebar.groups.subc'),             icon: HardHat,         keys: ['subcontractors', 'subcontracts', 'subcontract_claims'] },
    { id: 'finance_gl',       label: t('sidebar.groups.finance_gl', 'General Accounting'),      icon: BookOpen,   keys: ['accounts', 'journal', 'cancellations_log', 'expenses', 'credit_notes', 'accounting_reports', 'overhead_report', 'month_end', 'period_reopen', 'year_end', 'external_accountant'] },
    { id: 'finance_banking',  label: t('sidebar.groups.finance_banking', 'Banking & Treasury'),  icon: Landmark,   keys: ['bank', 'petty_cash', 'bank_reconciliation', 'bank_rules', 'bank_statements_registry', 'bank_import_manager', 'bank_adjustments', 'reconciliation_center', 'financial_repair_center', 'admin_financial_repair', 'admin_coa_classification'] },
    { id: 'finance_payments', label: t('sidebar.groups.finance_payments', 'Payments'),           icon: Banknote,   keys: ['payment_queue', 'payment_runs'] },
    { id: 'finance_tax',      label: t('sidebar.groups.finance_tax', 'Tax & Compliance'),        icon: Calculator, keys: ['vat_returns', 'zatca', 'zatca_setup', 'zatca_compliance', 'compliance_center', 'audit_trail'] },
    { id: 'finance_intel',    label: t('sidebar.groups.finance_intel', 'Financial Intelligence'), icon: Brain,     keys: ['matching_intelligence', 'autopost_insights', 'financial_timelines', 'data_quality_center'] },
    { id: 'pece',             label: t('sidebar.groups.pece', 'PECE Workspace'),                  icon: Brain,     keys: ['s2p_review', 's2p_dashboard', 'mar_review', 'mir_review', 'deliveries', 'consumption', 'pece_claims', 'dvl_truth', 'ecp_inspector', 'demo_center'] },
    { id: 'hr',               label: t('sidebar.groups.hr'),               icon: Users,           keys: ['employees', 'payroll', 'employee_accounts'] },
    { id: 'inv',              label: t('sidebar.groups.inv'),              icon: Package,         keys: ['inventory', 'assets', 'fleet'] },
    { id: 'docs',             label: t('sidebar.groups.docs'),             icon: Folder,          keys: ['documents', 'document_hub'] },
    { id: 'field',            label: t('sidebar.groups.field'),            icon: MapPin,          keys: ['visit_form', 'visit_reports_system', 'field_visits'] },
    { id: 'admin',            label: t('sidebar.groups.admin'),            icon: ShieldCheck,     keys: ['governance', 'audit_log', 'audit_intelligence', 'data_integrity', 'legacy_audit', 'security', 'security_dashboard', 'users', 'settings', 'backup', 'storage_health', 'archive_center', 'onboarding', 'migration_center', 'system_health', 'data_health', 'admin_records_maintenance'] },
  ];

  // Filter menu items based on permissions
  const visibleItems = allMenuItems.filter(item => {
    if (isAdmin) return true;
    return userPermissions.includes(item.key);
  });
  const visibleByKey = new Map(visibleItems.map(i => [i.key, i]));

  // Auto-expand: with flyout UI, we keep groups closed by default; the
  // parent buttons highlight the current section already. Hover/click opens
  // the flyout containing the children.
  const [openGroup, setOpenGroup] = useState(null);
  // For positioning the fixed flyout panel under each parent button.
  const [flyoutTop, setFlyoutTop] = useState(0);
  // Pinned flyout: when the user clicks (rather than hovers) the parent
  // group, we keep the flyout open even after the cursor leaves the sidebar.
  // Reset on outside click via the global handler below.
  const [pinnedGroup, setPinnedGroup] = useState(null);

  // Close any pinned flyout when the user clicks anywhere outside the sidebar.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!pinnedGroup) return;
      const aside = document.querySelector('aside');
      if (aside && !aside.contains(e.target)) {
        setPinnedGroup(null);
        setOpenGroup(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pinnedGroup]);

  // Close any pinned flyout whenever the user navigates to a new route.
  // Auto-opening flyouts on active-route was attempted but caused
  // pointer-event interception on main-content action buttons
  // (iter-102 testing-agent report). The active item is already
  // visually highlighted inside the collapsed sidebar — no flyout
  // needed for orientation.
  useEffect(() => {
    setPinnedGroup(null);
    setOpenGroup(null);
  }, [location.pathname]);

  // Title for the topbar
  const currentLabel = allMenuItems.find(item => item.path === location.pathname)?.label || 'Dashboard';

  const handleLogout = async () => {
    try { await axiosInstance.post('/auth/logout'); } catch (_) {}
    ['token', 'user', 'refresh_token', 'session_id'].forEach(k => localStorage.removeItem(k));
    navigate('/login');
  };

  // Hard-refresh the app: clears Service Worker + Cache Storage + reloads.
  // Does NOT touch any data in the database — only the cached frontend assets.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefreshApp = async () => {
    if (!window.confirm('Update the application to the latest version?\n\nNote: No data will be lost.')) return;
    setRefreshing(true);
    try {
      // 1) Unregister all service workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      // 2) Clear Cache Storage (PWA caches, fetched JS/CSS bundles)
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch (e) {
      // continue regardless — reload below is the actual update step
    } finally {
      // 3) Force reload with cache bypass. Append a timestamp to defeat any
      // intermediate proxy / browser caches that ignore the no-cache hint.
      const url = new URL(window.location.href);
      url.searchParams.set('_v', Date.now().toString());
      window.location.replace(url.toString());
    }
  };

  return (
    <div className="app-shell flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {showPilotTour && (
        <PilotWelcomeTour user={user} onClose={() => setShowPilotTour(false)} />
      )}
      {/* Sidebar */}
      <aside 
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed lg:relative lg:translate-x-0 z-30 w-64 h-full bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out shadow-xl`}
        data-testid={usePilotSidebarFinal ? 'sidebar-pilot' : 'sidebar-full'}
      >
        {usePilotSidebarFinal ? (
          <PilotSidebar user={user} onClose={() => setSidebarOpen(false)} />
        ) : (
        <div className="flex flex-col h-full">
          {/* Logo + Tenant Identity (dynamic — never leak cross-tenant branding) */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <img 
                  src="/logo.png" 
                  alt="Logo" 
                  className="w-10 h-10 object-contain"
                />
                <div className="min-w-0">
                  <h2 className="font-bold text-gray-900 text-sm truncate uppercase tracking-wide">
                    {tenantIdentity?.name || 'DISTINCT HIGH ROAD'}
                  </h2>
                  {tenantIdentity?.vat_number && (
                    <p className="text-xs text-gray-500 truncate">VAT: {tenantIdentity.vat_number}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation — grouped, with flyout sub-menu */}
          {/*
            Behaviour change (2026-02): clicking a parent group opens an
            absolutely-positioned flyout panel anchored to the sidebar edge,
            instead of expanding sub-items in place. This prevents tall groups
            (Accounting & Finance, Projects) from pushing other groups out of
            view and matches how production ERPs (SAP, Odoo) navigate.

            • Hover desktop  → flyout appears.
            • Click (any)    → flyout pins (stays open).
            • Mouse leave    → unpinned flyout auto-closes after a tiny delay.
            • Click outside  → pinned flyout closes.
          */}
          <nav className="flex-1 p-3 overflow-y-auto relative">
            <div className="space-y-1">
              {MENU_GROUPS.map((group) => {
                const items = group.keys.map(k => visibleByKey.get(k)).filter(Boolean);
                if (items.length === 0) return null;
                const Icon = group.icon;
                const hasActive = items.some(it => it.path === location.pathname);
                const isOpen = openGroup === group.id;
                return (
                  <div
                    key={group.id}
                    className="select-none relative group/menu"
                    onMouseEnter={(e) => {
                      // capture button rect so the fixed-position flyout can
                      // align with the parent button regardless of scroll.
                      const r = e.currentTarget.getBoundingClientRect();
                      setFlyoutTop(r.top);
                      setOpenGroup(group.id);
                    }}
                    onMouseLeave={() => { if (!pinnedGroup) setOpenGroup(null); }}
                  >
                    <button
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setFlyoutTop(r.top);
                        setPinnedGroup(pinnedGroup === group.id ? null : group.id);
                        setOpenGroup(group.id);
                      }}
                      data-testid={`group-${group.id}`}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg font-bold text-sm transition ${
                        hasActive
                          ? 'bg-purple-50 text-purple-700'
                          : isOpen
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className="flex items-center gap-2"><Icon size={16} /> {group.label}</span>
                      <ChevronRight size={14} className={`text-gray-400 transition-transform ${isOpen ? 'translate-x-0.5' : ''}`} />
                    </button>

                    {isOpen && (
                      <div
                        data-testid={`flyout-${group.id}`}
                        // Fixed positioning so the flyout escapes the sidebar's
                        // `overflow-y-auto` clipping. The sidebar is pinned to
                        // the left of the viewport in BOTH languages (see
                        // `.app-shell` rule in index.css), so the flyout
                        // always opens to the right.
                        className="fixed z-[1000] w-64 bg-white rounded-xl shadow-2xl border border-gray-200 py-2"
                        style={{
                          top: Math.max(8, flyoutTop),
                          left: '16.5rem',
                          maxHeight: 'calc(100vh - 1rem)',
                          overflowY: 'auto',
                          direction: document.documentElement.getAttribute('dir') === 'rtl' ? 'rtl' : 'ltr',
                        }}
                        onMouseEnter={() => setOpenGroup(group.id)}
                        onMouseLeave={() => { if (!pinnedGroup) setOpenGroup(null); }}
                      >
                        <div className="px-3 py-2 border-b border-gray-100 mb-1">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                            <Icon size={12} /> {group.label}
                          </p>
                        </div>
                        {items.map(item => {
                          const ItemIcon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                              onClick={() => {
                                setSidebarOpen(false);
                                setPinnedGroup(null);
                                setOpenGroup(null);
                              }}
                              className={`flex items-center gap-2 mx-2 px-3 py-2 rounded-lg text-sm transition ${
                                isActive
                                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow font-semibold'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <ItemIcon className="w-4 h-4" />
                              {item.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{user?.full_name || 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.role || 'Role'}</p>
              </div>
            </div>
            
            {/* Install App Button - Hidden if already installed */}
            {!isAppInstalled && (
              <button
                onClick={handleInstallClick}
                data-testid="install-app-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 transition-colors font-medium mb-2 shadow-lg"
              >
                <Download className="w-4 h-4" />
                {t('topbar.install_app')}
              </button>
            )}
            
            {/* Profile Button */}
            <Link
              to="/profile"
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors font-medium mb-2"
            >
              <User className="w-4 h-4" />
              {t('topbar.my_profile')}
            </Link>
            
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors font-medium"
            >
              <LogOut className="w-4 h-4" />
              {t('topbar.logout')}
            </button>
          </div>
        </div>
        )}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* P3 — Continuous CFO Signal Live Strip (polls every 45s). */}
        <CFOLiveStrip />
        {/* Phase 8 — global Period Reopen banner (renders only when a window is active). */}
        <ReopenActiveBanner />
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="lg:block hidden">
              <h1 className="text-2xl font-bold text-gray-900">
                {currentLabel}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <CompanySwitcher />
              <CommandBarLauncher />
              <LanguageToggle />
              <NotificationBell />
              <button
                onClick={handleRefreshApp}
                disabled={refreshing}
                data-testid="refresh-app-btn"
                title="Update app to latest version"
                className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">{refreshing ? t('topbar.updating') : t('topbar.update_app')}</span>
              </button>
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </header>

        {/* P6.1 — Critical Alerts Banner */}
        {criticalAlerts > 0 && !bannerDismissed && (
          <div
            className="bg-gradient-to-r from-rose-700 to-rose-600 text-white px-6 py-2.5 flex items-center justify-between gap-4 shadow-md cursor-pointer hover:from-rose-800 hover:to-rose-700 transition"
            onClick={() => navigate('/audit-intelligence')}
            data-testid="critical-banner"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <ShieldAlert size={18} className="animate-pulse shrink-0" />
              <span className="text-sm font-semibold truncate">
                🚨 {criticalAlerts === 1
                  ? 'تنبيه مالي حرج واحد مفتوح / 1 critical financial alert open'
                  : `${criticalAlerts} تنبيهات مالية حرجة مفتوحة / ${criticalAlerts} critical financial alerts open`}
                {' — '}<span className="underline">{t('layout.click_to_review')}</span>
              </span>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setBannerDismissed(true); }}
              className="p-1.5 rounded hover:bg-white/15 shrink-0 ms-2"
              data-testid="banner-dismiss"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          <div className="min-w-max">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;